/**
 * Merge enforced policy effects into tick breakdown/deltas for HUD feedback.
 */
import type { PolicyGameEffect, SimulationBreakdown, StatDeltas } from '../../../../types';
import type { Policy } from '../../../../types/policy';

export type PolicyTickTotals = {
  budget: number;
  compliance: number;
  threat: number;
};

export function sumPolicyTickEffects(effects: PolicyGameEffect[]): PolicyTickTotals {
  return effects.reduce(
    (acc, effect) => ({
      budget: acc.budget + effect.effects.budget,
      compliance: acc.compliance + effect.effects.compliance / 30,
      threat: acc.threat + effect.effects.threat,
    }),
    { budget: 0, compliance: 0, threat: 0 },
  );
}

export function mergePolicyIntoTickFeedback(
  breakdown: SimulationBreakdown,
  deltas: StatDeltas,
  policyTotals: PolicyTickTotals,
): { breakdown: SimulationBreakdown; deltas: StatDeltas } {
  const policyExpense = policyTotals.budget < 0 ? -policyTotals.budget : 0;
  const policyIncome = policyTotals.budget > 0 ? policyTotals.budget : 0;
  const policyCompGain = Math.max(0, policyTotals.compliance);
  const policyCompDrain = Math.max(0, -policyTotals.compliance);
  const policyThreatGain = Math.max(0, policyTotals.threat);
  const policyThreatMitigated = Math.max(0, -policyTotals.threat);

  return {
    breakdown: {
      income: {
        ...breakdown.income,
        buildings: breakdown.income.buildings + policyIncome,
        total: breakdown.income.total + policyIncome,
      },
      expenses: {
        ...breakdown.expenses,
        maintenance: breakdown.expenses.maintenance + policyExpense,
        total: breakdown.expenses.total + policyExpense,
      },
      compliance: {
        ...breakdown.compliance,
        generated: breakdown.compliance.generated + policyCompGain,
        decay: breakdown.compliance.decay + policyCompDrain,
        net: breakdown.compliance.net + policyTotals.compliance,
      },
      threat: {
        ...breakdown.threat,
        generated: breakdown.threat.generated + policyThreatGain,
        mitigated: breakdown.threat.mitigated + policyThreatMitigated,
        net: breakdown.threat.net + policyTotals.threat,
      },
    },
    deltas: {
      budget: deltas.budget + policyTotals.budget,
      compliance: deltas.compliance + policyTotals.compliance,
      threat: deltas.threat + policyTotals.threat,
    },
  };
}

export function formatPolicyEffectNews(
  effect: PolicyGameEffect,
  policy?: Policy,
): string {
  const name = policy?.name ?? effect.policyId;
  const parts: string[] = [];
  const compTick = effect.effects.compliance / 30;
  if (Math.abs(compTick) >= 0.05) {
    parts.push(`${compTick >= 0 ? '+' : ''}${compTick.toFixed(1)}% compliance/tick`);
  }
  if (Math.abs(effect.effects.threat) >= 0.05) {
    parts.push(
      `${effect.effects.threat >= 0 ? '+' : ''}${effect.effects.threat.toFixed(1)} threat/tick`,
    );
  }
  if (Math.abs(effect.effects.budget) >= 1) {
    const budgetRounded = Math.round(effect.effects.budget);
    parts.push(
      budgetRounded >= 0
        ? `+$${budgetRounded}/tick`
        : `-$${Math.abs(budgetRounded)}/tick`,
    );
  }
  if (parts.length === 0) {
    parts.push('monitoring active');
  }
  return `POLICY ACTIVE: ${name} — ${parts.join(', ')}`;
}